import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { useAuth0, AuthorizationParams } from '@auth0/auth0-react'

import { useAuthRedirectManager } from './use-auth-redirect'

const authNotificationFlags = [
  'westfield_session_expired',
  'westfield_password_reset_expired',
  'westfield_password_reset_success',
  'westfield_user_is_blocked',
  'westfield_generic_issue',
] as const

type AuthNotificationFlag = (typeof authNotificationFlags)[number]

function isAuthNotificationFlag(flag: unknown): flag is AuthNotificationFlag {
  return typeof flag === 'string' && authNotificationFlags.includes(flag as any)
}

export const authorizationParamsDefault = {
  response_type: 'token id_token',
  connection: 'IdentityService',
  scope: 'openid email profile email_verified',
}

const useLoginRedirect = (): ((
  authorizationParams?: AuthorizationParams
) => void) => {
  const { query } = useRouter()
  const { loginWithRedirect } = useAuth0()
  const { saveFromQuery } = useAuthRedirectManager()

  const flagFromQuery = query.notificationFlag
  let flagForParams: { [K in AuthNotificationFlag]?: 'true' } | undefined

  if (isAuthNotificationFlag(flagFromQuery)) {
    flagForParams = { [flagFromQuery]: 'true' }
  }

  return useCallback(
    (authorizationParams?: AuthorizationParams) => {
      saveFromQuery()

      loginWithRedirect({
        authorizationParams: {
          ...authorizationParamsDefault,
          ...authorizationParams,
          ...flagForParams,
        },
        async openUrl(url) {
          window.location.replace(url)
        },
      })
    },
    [flagForParams, loginWithRedirect, saveFromQuery]
  )
}

export default useLoginRedirect
